<template>
  <main
    role="main"
    class="col content-spacing"
    style="max-width: 88% !important;"
  >
    <PageHeader
      pageTitle="Faturas Atrasadas"
      class="page-header-overdue-invoice"
    >
      <div style="display: flex; gap: 0.5rem;">
        <GenericSearchInput v-model="search.field" />
        <SelectMonth v-model="search.month" />
        <SelectYear v-model="search.year" />
        <SelectAccountManager v-model="search.accountManagerId" />
        <SelectPromoter
          :traderList="traderList"
          v-model="search.trader"
          v-if="overdueInvoiceStatusList.length"
        />
        <SelectInstallationStatus v-model="search.installationStatus" />
      </div>
    </PageHeader>
    <b-skeleton-table
      animation="wave"
      style="margin-top: 2rem;"
      :rows="5"
      :columns="15"
      :table-props="{ bordered: true, striped: true }"
      v-if="isLoading"
    />
    <section
      style="margin-bottom: 2rem;"
      v-if="overdueInvoiceStatusList.length && !isLoading"
    >
      <OverdueInvoiceSummaryTable
        :overdueInvoiceStatusList="overdueInvoiceStatusList"
      />
    </section>
    <section
      v-if="!isLoading"
      v-for="(invoiceStatus, index) of overdueInvoiceStatusList"
      :key="`${invoiceStatus.name}_${index}`"
    >
      <div
        style="display: flex; align-items: center; gap: 2rem; margin-top: 1rem;"
        v-if="invoiceStatus.list.length"
      >
        <h3>{{ invoiceStatus.name }}</h3>
        <vue-json-to-csv
          :json-data="invoiceStatus.csvList"
          :separator="';'"
          :csv-title="`${invoiceStatus.name}_CSV`"
        >
          <BaseButtonComponent block variant="primary-action">
            CSV
          </BaseButtonComponent>
        </vue-json-to-csv>
      </div>
      <OverdueInvoiceTable
        :list="invoiceStatus.list"
        @changeInvoiceToAnotherJudicialTable="
          changeInvoiceToAnotherJudicialTable
        "
        @sendInvoiceToSmallClaims="sendInvoiceToSmallClaims"
        @sendInvoiceToSerasa="sendInvoiceToSerasa"
        @sendInvoiceToProtest="sendInvoiceToProtest"
        :isLoading="isLoading"
      />
    </section>
  </main>
</template>

<script>
/// <reference path="../../modules/invoice/types/index.js" />

import {
  OverdueInvoiceSummaryTable,
  OverdueInvoiceTable,
} from '../../modules/invoice/components';
import { PageHeader } from '../../ui/page-header';
import {
  calculateDaysDifferenceFromNow,
  formatCurrencyFraction2,
  dateWithoutHourFormat,
  formatSocialNumber,
} from '../../utils';
import { getCommunicationStep } from '../../modules/invoice/utils';
import { Api } from '../../vue/api';
import helpers from '../../vue/helpers';
import { GenericSearchInput } from '../../ui/inputs';
import {
  SelectAccountManager,
  SelectMonth,
  SelectPromoter,
  SelectYear,
} from '../../ui/selects';
import VueJsonToCsv from 'vue-json-to-csv';
import { JUDICIAL_STATUS } from '../../modules/invoice/constants';
import { SelectInstallationStatus } from '../../modules/installation/components/index';
import { BaseButtonComponent } from '../../ui/buttons';
const API = new Api();

export default {
  components: {
    PageHeader,
    VueJsonToCsv,
    SelectPromoter,
    GenericSearchInput,
    OverdueInvoiceTable,
    OverdueInvoiceSummaryTable,
    SelectMonth,
    SelectYear,
    SelectAccountManager,
    SelectInstallationStatus,
    BaseButtonComponent,
  },
  data() {
    return {
      overdueInvoiceList: [],
      traderList: [],
      overdueInvoiceStatusList: [],
      isLoading: false,
      search: {
        field: '',
        month: null,
        year: null,
        trader: null,
        accountManagerId: null,
        installationStatus: null,
      },
    };
  },
  computed: {
    month() {
      return this.search.month;
    },
    year() {
      return this.search.year;
    },
    field() {
      return this.search.field;
    },
    trader() {
      return this.search.trader;
    },
    accountManagerId() {
      return this.search.accountManagerId;
    },
    installationStatus() {
      return this.search.installationStatus;
    },
  },
  watch: {
    async month() {
      await this.getAll();
    },
    async year() {
      await this.getAll();
    },
    async field() {
      await this.getAll();
    },
    async trader() {
      await this.getAll();
    },
    async accountManagerId() {
      await this.getAll();
    },
    async installationStatus() {
      await this.getAll();
    },
  },
  async mounted() {
    await this.getAll();
  },
  methods: {
    /**
     * Recupera todas as faturas vencidas e calcula os dias de atraso e a etapa de comunicação para cada fatura.
     * @async
     * @returns {Promise<void>} Uma promessa que é resolvida quando as faturas são recuperadas e processadas.
     */
    async getAll() {
      this.isLoading = true;
      /** @type {{model: OverdueInvoice[], ok: boolean, errors: Object}} */
      const { model, ok, errors } = await API.post('/execute', {
        path:
          '/overdue-invoice' +
          helpers.toQueryString({
            // TODO: alterar para o estilo de paginação da tela de instalação
            search: JSON.stringify(this.search),
          }),
        method: 'get',
      });
      if (!ok) {
        this.$bvToast.toast(errors.message, {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
        this.isLoading = false;
        return;
      }

      const overdueInvoiceClientDomain = model.map(overdueInvoice => {
        const delayedDays =
          calculateDaysDifferenceFromNow(overdueInvoice.dueDate) - 1;
        const passo = getCommunicationStep(delayedDays);
        return {
          ...overdueInvoice,
          delayedDays,
          passo,
        };
      });
      this.overdueInvoiceList = overdueInvoiceClientDomain;
      this.overdueInvoiceStatusList = this.generateOverdueInvoiceStatusList(
        overdueInvoiceClientDomain,
      );

      const traderList = [];
      for (const overdueInvoice of model) {
        if (overdueInvoice.nameTrader) {
          traderList.push(overdueInvoice.nameTrader);
        }
      }
      this.traderList = traderList;

      this.$bvToast.toast('Faturas recuperadas com sucesso', {
        title: `Sucesso`,
        variant: 'success',
        solid: true,
      });
      this.isLoading = false;
    },
    /**
     * Gera uma lista de status de faturas vencidas, categorizando as faturas com base no status judicial.
     *
     * @param {OverdueInvoice[]} overdueInvoiceList - A lista de faturas vencidas.
     * @returns {Object[]} Uma lista de objetos, cada um representando um status judicial e contendo as faturas correspondentes e uma lista CSV.
     * @returns {Object.name} name - O nome do status judicial.
     * @returns {OverdueInvoice[]} list - A lista de faturas vencidas para o status judicial.
     * @returns {OverdueInvoiceCSV[]} csvList - A lista de faturas em formato CSV para o status judicial.
     *
     * @example
     * // Exemplo de uso
     * const overdueInvoiceStatusList = generateOverdueInvoiceStatusList(overdueInvoiceList);
     * console.log(overdueInvoiceStatusList);
     */
    generateOverdueInvoiceStatusList(overdueInvoiceList) {
      const overdueInvoiceStatusList = [];
      for (const [key, value] of Object.entries(JUDICIAL_STATUS)) {
        const list =
          key === 'LATE'
            ? overdueInvoiceList.filter(
                invoice =>
                  invoice.judicialStatus === '' ||
                  invoice.judicialStatus === null ||
                  invoice.judicialStatus === 'LATE',
              )
            : overdueInvoiceList.filter(
                invoice => invoice.judicialStatus === key,
              );
        const csvList = list.length
          ? this.parseOverdueInvoiceListToCSVList(list)
          : [];
        overdueInvoiceStatusList.push({
          name: value,
          list,
          csvList,
        });
      }
      return overdueInvoiceStatusList;
    },
    /**
     * Realiza o parse de uma lista de Faturas Vencidas para o seu formato em CSV
     * @param {OverdueInvoice[]} overdueInvoiceList
     * @returns {function(): OverdueInvoiceCSV[] | []} Uma promessa que é resolvida quando as faturas são recuperadas e processadas.
     */
    parseOverdueInvoiceListToCSVList(overdueInvoiceList) {
      return overdueInvoiceList.map(invoice => ({
        Instalação: invoice.installationNumber,
        Consumidor: invoice.name,
        BoletoVeins: formatCurrencyFraction2(invoice.boletoVeins),
        Vencimento: dateWithoutHourFormat(invoice.dueDate),
        Dias: invoice.delayedDays,
        Passo: invoice.passo,
        Celular: invoice.phone,
        Observação: invoice.observation || '',
        Promotor: invoice.nameTrader || '',
        'Cod Boleto': invoice.linhaDigitavel,
        Status: JUDICIAL_STATUS[invoice.judicialStatus],
        'Economia (R$)': formatCurrencyFraction2(invoice.consumerEconomy) || 0,
        'Consumo Total (kWh)':
          formatCurrencyFraction2(invoice.totalConsumption) || 0,
        'Energia Compensada (kWh)':
          formatCurrencyFraction2(invoice.compensatedEnergy) || 0,
        'Saldo Acumulado (kWh)':
          formatCurrencyFraction2(invoice.accumulatedBalance) || 0,
        'CPF/CNPJ': formatSocialNumber(invoice.socialNumber),
        Distribuidora: invoice.distributorName,
        UF: invoice.state,
        Cidade: invoice.city,
        Bairro: invoice.district,
        Endereço: invoice.address,
        CEP: invoice.zipCode,
        Email: invoice.email,
        ERPID: invoice.erpId,
      }));
    },
    async changeInvoiceToAnotherJudicialTable(input) {
      this.isLoading = true;
      /** @type {{ok: boolean, errors: Object}} */
      const { ok, errors } = await API.post('/execute', {
        path: `/invoice/change-judicial-status/${input.id}`,
        method: 'post',
        data: { judicialStatus: input.judicialStatus },
      });
      if (!ok) {
        this.$bvToast.toast(errors.message, {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
        this.isLoading = false;
        return;
      }
      this.$bvToast.toast('Status da cobrança atualizado com sucesso!', {
        title: `Sucesso`,
        variant: 'success',
        solid: true,
      });
      await this.getAll();
      this.isLoading = false;
    },
    /**
     * Registra a fatura atrasada nas pequenas causas.
     *
     * @param {number} id - O ID da fatura.
     * @returns {Promise<void>} A promise that resolves when the operation is complete.
     */
    async sendInvoiceToSmallClaims(id) {
      this.isLoading = true;
      /** @type {{ok: boolean, errors: Object}} */
      const { ok, errors } = await API.post('/execute', {
        path: `/invoice/send-to-small-claims/${id}`,
        method: 'post',
      });
      if (!ok) {
        this.$bvToast.toast(errors.message, {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
        this.isLoading = false;
        return;
      }
      this.$bvToast.toast(
        'Fatura registrada nas Pequenas Causas com sucesso!',
        {
          title: `Sucesso`,
          variant: 'success',
          solid: true,
        },
      );
      await this.getAll();
      this.isLoading = false;
    },
    /**
     * Registra a fatura atrasada no Serasa.
     *
     * @param {number} id - O ID da fatura.
     * @returns {Promise<void>} A promise that resolves when the operation is complete.
     */
    async sendInvoiceToSerasa(id) {
      this.isLoading = true;
      /** @type {{ok: boolean, errors: Object}} */
      const { ok, errors } = await API.post('/execute', {
        path: `/invoice/send-to-serasa/${id}`,
        method: 'post',
      });
      if (!ok) {
        this.$bvToast.toast(errors.message, {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
        this.isLoading = false;
        return;
      }
      this.$bvToast.toast('Fatura registrada no Serasa com sucesso!', {
        title: `Sucesso`,
        variant: 'success',
        solid: true,
      });
      await this.getAll();
      this.isLoading = false;
    },
    /**
     * Registra a fatura atrasada no Protesto.
     *
     * @param {number} id - O ID da fatura.
     * @returns {Promise<void>} A promise that resolves when the operation is complete.
     */
    async sendInvoiceToProtest(id) {
      this.isLoading = true;
      /** @type {{ok: boolean, errors: Object}} */
      const { ok, errors } = await API.post('/execute', {
        path: `/invoice/send-to-protest/${id}`,
        method: 'post',
      });
      if (!ok) {
        this.$bvToast.toast(errors.message, {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
        this.isLoading = false;
        return;
      }
      this.$bvToast.toast('Fatura Protestada com sucesso!', {
        title: `Sucesso`,
        variant: 'success',
        solid: true,
      });
      await this.getAll();
      this.isLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.page-header-overdue-invoice {
  padding-top: 1rem;
}
</style>
